import { useMemo, useState } from "react";
import { useRouter } from "next/router";
import WineReview from "./WineReview";
import { parse } from "date-fns";
import { Wine } from "../../../../context/wineContext/reducer";

const parseArticleDate = (date: string): Date => {
  return parse(date, "MMMM yyyy", new Date());
};

const sortByVintageThenArticleDate = (l: Wine, r: Wine) => {
  if (l.vintage === r.vintage) {
    return parseArticleDate(l.article_date) <
      parseArticleDate(r.article_date)
      ? 1
      : -1;
  } else {
    return l.vintage < r.vintage ? 1 : -1;
  }
};

const WineReviewList = (props: {
  activeWine?: any;
  wines: any;
  vintage?: any;
  handleSelectedReviews: (wineId: number) => void;
}) => {
  const router = useRouter();
  const { activeWine, wines, vintage, handleSelectedReviews } = props;
  const { slug } = router.query;
  const [open, setOpen] = useState(false);

  const sortedWines = useMemo(() => {
    // Only show reviews where article_published is true
    const winesFiltered = wines.filter((wine) => wine.wine.article_published);
    return winesFiltered.sort((l, r) => sortByVintageThenArticleDate(l.wine, r.wine));
  }, [wines]);

  // If we have a vintage, we just sort and list all reviews from that vintage
  if (vintage) {
    return (
      <>
        {sortedWines
          .filter((wine) => wine.wine.vintage == vintage[0])
          .map((review, idx) => (
            <WineReview key={idx} review={review.wine} handleSelectedReviews={handleSelectedReviews}/>
          ))}
      </>
    );
    // Else, we want to lift the active wine/review to the top and
    // collapse the rest of the reviews behind a toggle
  } else if (activeWine && Object.keys(activeWine).length !== 0) {
    // We filter out the active wine by slug
    const winesReviewsSorted = sortedWines
      .filter((wine) => wine.wine.slug !== slug[0])
      .map((review, idx) => (
        <WineReview showVintage key={idx} review={review.wine} handleSelectedReviews={handleSelectedReviews}/>
      ));

    return (
      <>
        <WineReview key={0} showVintage review={activeWine} handleSelectedReviews={handleSelectedReviews}/>
        <div
          className={`no-print w-full flex justify-center p-4 ${
            open ? "rounded-t-md" : "rounded-md"
          } font-semibold border text-red cursor-pointer hover:bg-red hover:text-white`}
          onClick={(_) => setOpen(!open)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setOpen(!open);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <span className={"text-center"}>
            {open ? "Hide reviews" : "See more reviews"}
          </span>
        </div>
        <div
          className={`border text-grey font-light ${
            open ? "visible" : "hidden"
          } p-4 rounded-b-md space-y-2`}
        >
          {winesReviewsSorted}
        </div>
      </>
    );
  } else {
    const newestWine = wines?.reduce((latest, w) => {
      return (!latest || w.wine.vintage > latest?.vintage) ? w.wine : latest;
    }, null);
    return (
      <>
        <WineReview key={0} showVintage review={newestWine} handleSelectedReviews={handleSelectedReviews}/>
        <div
          className={`w-full flex justify-center p-4 ${
            open ? "rounded-t-md" : "rounded-md"
          } font-semibold border text-red cursor-pointer hover:bg-red hover:text-white`}
          onClick={(_) => setOpen(!open)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setOpen(!open);
            }
          }}
          role="button"
          tabIndex={0}
        >
          <span className={"text-center"}>
            {open ? "Hide reviews" : "See more reviews"}
          </span>
        </div>
        <div
          className={`border text-grey font-light ${
            open ? "visible" : "hidden"
          } p-4 rounded-b-md space-y-2`}
        >
          {sortedWines
            .map((review, idx) => (
              <WineReview showVintage key={idx} review={review.wine} handleSelectedReviews={handleSelectedReviews}/>
            ))
            .slice(1)}
        </div>
      </>
    );
  }
};

export default WineReviewList;
